import { SalesOrderDeletedEvent } from '@rematter/frontend-lib-api-client/grpc/trade/salesOrder_events_pb';
import { SalesOrderStatus } from '@rematter/frontend-lib-api-client/grpc/trade/salesOrder_pb';
import {
  BadgeField,
  Col,
  FieldDisplay,
  humanReadableDateTime,
  IDField,
  ThemeColor,
} from '@rematter/ui-web';

import { salesOrderTypeLabels, salesOrderStatusLabels } from '../enumLabels';

import { protoTimestampToZonedDateTimeString } from '@/audit-log/_components/_shared';
import { getLocale } from '~/shared/utils/locale';

const getStatusColor = (status: SalesOrderStatus): ThemeColor => {
  switch (status) {
    case SalesOrderStatus.DRAFT:
      return 'neutral';
    case SalesOrderStatus.OPEN:
      return 'primary';
    case SalesOrderStatus.FINISHED:
      return 'success';
    case SalesOrderStatus.VOIDED:
      return 'failure';
    default:
      return 'neutral';
  }
};

type SalesOrderDeletedDetailProps = {
  event: SalesOrderDeletedEvent;
};

export const SalesOrderDeletedDetail = ({
  event,
}: SalesOrderDeletedDetailProps) => {
  const salesOrder = event.salesOrder;

  if (!salesOrder) {
    return (
      <Col className="@SalesOrderDeletedDetail items-center justify-center p-4 text-neutral/40">
        <span>No data available</span>
      </Col>
    );
  }

  const startDateStr = protoTimestampToZonedDateTimeString(
    salesOrder.startDate,
  );
  const endDateStr = protoTimestampToZonedDateTimeString(salesOrder.endDate);

  return (
    <Col className="@SalesOrderDeletedDetail gap-3" gap3>
      <Col
        className="rounded-md border border-failure/20 bg-failure/5 p-3"
        gap2
      >
        <span className="text-xs font-medium text-failure/80">
          Deleted Sales Order
        </span>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
          <IDField label="ID" value={salesOrder.id?.value} />
          <FieldDisplay
            label="Document Number"
            value={salesOrder.documentNumber}
          />
          <BadgeField
            label="Type"
            value={salesOrderTypeLabels[salesOrder.type]}
            color="teal"
          />
          <BadgeField
            label="Status"
            value={salesOrderStatusLabels[salesOrder.status]}
            color={getStatusColor(salesOrder.status)}
          />
          <IDField label="Facility ID" value={salesOrder.facilityId?.value} />
          <IDField label="Account ID" value={salesOrder.accountId?.value} />
          <IDField label="Contact ID" value={salesOrder.contactId?.value} />
          <FieldDisplay
            label="Counterparty Reference"
            value={salesOrder.counterpartyReferenceNumber}
          />
          <FieldDisplay
            label="Payment Currency"
            value={salesOrder.paymentCurrency}
          />
          {startDateStr && (
            <FieldDisplay
              label="Start Date"
              value={humanReadableDateTime(startDateStr, {
                locale: getLocale(),
              })}
            />
          )}
          {endDateStr && (
            <FieldDisplay
              label="End Date"
              value={humanReadableDateTime(endDateStr, { locale: getLocale() })}
            />
          )}
        </div>
      </Col>
    </Col>
  );
};
